import { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Typography,
    CircularProgress,
    Grid,
    Stack
} from '@mui/material';
import { Visibility, AttachFile, Edit } from '@mui/icons-material';
import Chip from '@mui/material/Chip';
import { getTypeDocuments } from 'services/contracts';
import ErrorIcon from '@mui/icons-material/Error';
import UploadDocumentFiles from 'components/UploadDocumentFiles';
import DocumentFiles from 'components/DocumentFiles';
import DocumentStatus from 'components/DocumentStatus';
import moment from 'moment';
import DocumentNotification from 'components/DocumentNotification';
import { IconMessage2 } from '@tabler/icons';
import CircleErrorIcon from '@mui/icons-material/Error';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { documentStatus } from 'constants/documentStatus';

const TableDocuments = ({ contractSelected }) => {
    const [openValidate, setOpenValidate] = useState(false);
    const [openUploadModal, setOpenUploadModal] = useState(false);
    const [openFilesModal, setOpenFilesModal] = useState(false);
    const [openNotificationModal, setOpenNotificationModal] = useState(false);

    const [allDocuments, setAllDocuments] = useState([]);

    const [documentSelected, setDocumentSelected] = useState(null);
    const [typeDocumentSelected, setTypeDocumentSelected] = useState(null);

    const [loading, setLoading] = useState(false);

    const user = useSelector((state) => state.auth.user);

    const onlyView = user.perfil_id === 4;

    const getDocuments = () => {
        const group_documents = 1;
        setLoading(true);
        getTypeDocuments(group_documents, contractSelected).then((resp) => {
            setAllDocuments(resp.data);
            setLoading(false);
        });
    };

    const renderStatus = (status) => {
        let color, label;

        if (!status) {
            const notRequired = documentStatus.find((item) => item.status === 'N/S');
            color = notRequired.color;
            label = notRequired.status;
        }

        if (status) {
            const docStatus = documentStatus.find((item) => item.status === status);
            color = docStatus.color;
            label = docStatus.status;
        }

        return (
            <Chip
                sx={{
                    width: 100,
                    backgroundColor: color,
                    color: 'white'
                }}
                label={label}
            />
        );
    };

    const renderValidade = (validade) => {
        if (validade) {
            return <Chip sx={{ width: 110 }} color="primary" label={moment(validade).format('DD/MM/YYYY')} />;
        }
        return <Chip sx={{ width: 110 }} label="Sem validade" />;
    };

    useEffect(() => {
        if (contractSelected) {
            getDocuments();
        }
    }, [contractSelected]); // eslint-disable-line

    return (
        <Stack sx={{ pb: 2 }}>
            {allDocuments.length > 0 ? (
                <>
                    <TableContainer component={Paper} sx={{ position: 'relative' }}>
                        {loading && (
                            <Grid
                                container
                                sx={{
                                    position: 'absolute',
                                    width: '100%',
                                    height: '100%',
                                    zIndex: 100,
                                    top: 0,
                                    left: 0,
                                    backgroundColor: 'rgba(255, 255, 255, 0.8)'
                                }}
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                            >
                                <CircularProgress />
                            </Grid>
                        )}
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Documentos</TableCell>
                                    <TableCell width={150}>Status</TableCell>
                                    <TableCell width={150}>Validade</TableCell>
                                    <TableCell width={100}>Observações</TableCell>
                                    <TableCell width={100}>Visualizar</TableCell>
                                    <TableCell width={100}>Anexar</TableCell>
                                    {(user.perfil_id === 1 || user.perfil_id === 3) && <TableCell width={100}>Editar</TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {allDocuments?.map((document) => (
                                    <TableRow key={document.id}>
                                        <TableCell>
                                            <Typography variant="p">{document.nome}</Typography>
                                            {document.especificacoes && (
                                                <Typography variant="subtitle2" color="GrayText">
                                                    {document.especificacoes}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>{renderStatus(document.documento?.status)}</TableCell>
                                        <TableCell>{renderValidade(document.documento?.validade)}</TableCell>
                                        <TableCell>
                                            <IconButton
                                                disabled={document.documento === null || onlyView}
                                                onClick={() => {
                                                    setTypeDocumentSelected(document);
                                                    setDocumentSelected(document.documento);
                                                    setOpenNotificationModal(true);
                                                }}
                                                sx={{ position: 'relative' }}
                                            >
                                                <IconMessage2 />
                                                {document.documento?.novas_notificacoes == 1 && (
                                                    <CircleErrorIcon
                                                        color="error"
                                                        sx={{ position: 'absolute', top: 0, left: 0, fontSize: '15px' }}
                                                        //
                                                    />
                                                )}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                disabled={document.documento === null}
                                                onClick={() => {
                                                    setTypeDocumentSelected(document);
                                                    setDocumentSelected(document.documento);
                                                    setOpenFilesModal(true);
                                                }}
                                            >
                                                <Visibility />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>
                                            <div style={{ display: 'flex' }}>
                                                <IconButton
                                                    disabled={document?.documento?.status == 'VALIDADO' || onlyView}
                                                    onClick={() => {
                                                        setTypeDocumentSelected(document);
                                                        setDocumentSelected(document.documento);
                                                        setOpenUploadModal(true);
                                                    }}
                                                >
                                                    <AttachFile />
                                                </IconButton>
                                            </div>
                                        </TableCell>
                                        {(user.perfil_id === 1 || user.perfil_id === 3) && (
                                            <TableCell>
                                                <IconButton
                                                    disabled={document.documento === null || onlyView}
                                                    onClick={() => {
                                                        setDocumentSelected(document.documento);
                                                        setOpenValidate(true);
                                                    }}
                                                >
                                                    <Edit />
                                                </IconButton>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                                {allDocuments.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} style={{ textAlign: 'center' }}>
                                            Nenhum documento encontrado
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <DocumentStatus
                        onOpen={openValidate}
                        onClose={() => setOpenValidate(false)}
                        document={documentSelected}
                        reloadDocuments={() => {
                            getDocuments();
                        }}
                        handleComments={(document) => {
                            setDocumentSelected(document);
                            setOpenNotificationModal(true);
                        }}
                    />
                    <DocumentFiles
                        openModal={openFilesModal}
                        handleCloseModal={() => {
                            setOpenFilesModal(false);
                            setDocumentSelected(null);
                        }}
                        setSuccess={() => {}}
                        documentSelected={documentSelected}
                        typeDocumentSelected={typeDocumentSelected}
                        reloadDocuments={() => {
                            getDocuments();
                        }}
                    />
                    <UploadDocumentFiles
                        openModal={openUploadModal}
                        handleCloseModal={() => {
                            setOpenUploadModal(false);
                            setDocumentSelected(null);
                        }}
                        accept={['application/pdf']}
                        setSuccess={() => {}}
                        contractSelected={contractSelected}
                        typeDocumentSelected={typeDocumentSelected}
                        reloadDocuments={() => {
                            getDocuments();
                        }}
                    />
                    <DocumentNotification
                        open={openNotificationModal}
                        onClose={() => {
                            setOpenNotificationModal(false);
                            getDocuments();
                        }}
                        document={documentSelected}
                    />
                </>
            ) : contractSelected === null || contractSelected === '' ? (
                <Typography variant="h4" display="flex" justifyContent="center" alignItems="center" sx={{ py: 2, color: 'GrayText' }}>
                    Selecione um contrato
                    <ErrorIcon color="warning" />
                </Typography>
            ) : (
                <Typography variant="h4" display="flex" justifyContent="center" alignItems="center" sx={{ py: 2, color: 'GrayText' }}>
                    Nenhum documento encontrado
                </Typography>
            )}
        </Stack>
    );
};

TableDocuments.propTypes = {
    contractSelected: PropTypes.number
};

export default TableDocuments;
